import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { getPallets } from '@/lib/api';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Search, Filter, Package, MapPin, ChevronDown } from 'lucide-react';
import { CATEGORY_COLORS, CATEGORY_LABELS, STATUS_COLORS, STATUS_LABELS } from '@/lib/constants';

export default function InventoryPage() {
  const [searchParams] = useSearchParams();
  const initialStatus = searchParams.get('status') || 'active';
  const [pallets, setPallets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState(initialStatus);
  const [categoryFilter, setCategoryFilter] = useState('');
  const [rowFilter, setRowFilter] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const navigate = useNavigate();

  useEffect(() => { loadPallets(); }, [statusFilter]);

  const loadPallets = async () => {
    setLoading(true);
    try {
      const res = await getPallets({ status: statusFilter, limit: 500 });
      setPallets(res.data.pallets || []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const filtered = useMemo(() => {
    let result = pallets;
    if (search) {
      const s = search.toLowerCase();
      result = result.filter(p =>
        p.pallet_ref?.toLowerCase().includes(s) ||
        p.slot_address?.toLowerCase().includes(s) ||
        p.items?.some(i =>
          i.description?.toLowerCase().includes(s) ||
          i.item_code?.toLowerCase().includes(s) ||
          i.batch_no?.toLowerCase().includes(s) ||
          i.heat_no?.toLowerCase().includes(s)
        )
      );
    }
    if (categoryFilter) {
      result = result.filter(p => p.items?.some(i => i.category === categoryFilter));
    }
    if (rowFilter) {
      result = result.filter(p => p.slot_address?.startsWith(rowFilter));
    }
    return result;
  }, [pallets, search, categoryFilter, rowFilter]);

  return (
    <div className="space-y-4" data-testid="inventory-page">
      <div className="flex items-center gap-2">
        <h1 className="text-xl font-bold text-gray-900 flex-1">Inventory</h1>
        <Badge variant="outline" className="text-xs">{filtered.length} pallets</Badge>
      </div>

      {/* Search & Filters */}
      <div className="space-y-2">
        <div className="flex gap-2">
          <div className="relative flex-1">
            <Search size={16} className="absolute left-3 top-2.5 text-gray-400" />
            <Input
              placeholder="Search pallet, item, batch, location..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9 h-9"
              data-testid="inventory-search"
            />
          </div>
          <Button variant="outline" size="sm" onClick={() => setShowFilters(!showFilters)} data-testid="filter-toggle">
            <Filter size={14} className="mr-1" /> Filters
            <ChevronDown size={14} className={`ml-1 transition-transform ${showFilters ? 'rotate-180' : ''}`} />
          </Button>
        </div>

        {showFilters && (
          <div className="flex flex-wrap gap-2 animate-fadeIn">
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-32 h-8 text-xs" data-testid="filter-status">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="packing">Packing</SelectItem>
                <SelectItem value="ready">Ready</SelectItem>
                <SelectItem value="placed">Placed</SelectItem>
                <SelectItem value="partial">Partial</SelectItem>
                <SelectItem value="issued">Issued</SelectItem>
                <SelectItem value="void">Void</SelectItem>
                <SelectItem value="qc_hold">QC Hold</SelectItem>
              </SelectContent>
            </Select>
            <Select value={categoryFilter || "_all_"} onValueChange={v => setCategoryFilter(v === "_all_" ? "" : v)}>
              <SelectTrigger className="w-32 h-8 text-xs" data-testid="filter-category">
                <SelectValue placeholder="Category" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="_all_">All Categories</SelectItem>
                {Object.entries(CATEGORY_LABELS).map(([k, v]) => (
                  <SelectItem key={k} value={k}>{v}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={rowFilter || "_all_"} onValueChange={v => setRowFilter(v === "_all_" ? "" : v)}>
              <SelectTrigger className="w-24 h-8 text-xs" data-testid="filter-row">
                <SelectValue placeholder="Row" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="_all_">All Rows</SelectItem>
                {['A', 'B', 'C', 'D'].map(r => <SelectItem key={r} value={r}>Row {r}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        )}
      </div>

      {/* Pallet List */}
      {loading ? (
        <div className="flex justify-center py-20"><div className="animate-spin h-8 w-8 border-4 border-[#1a2fce] border-t-transparent rounded-full" /></div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-20 text-gray-400">
          <Package size={40} className="mx-auto mb-3 opacity-50" />
          <p className="text-sm">No pallets found</p>
        </div>
      ) : (
        <div className="space-y-2">
          {filtered.map((p) => {
            const categories = [...new Set(p.items?.map(i => i.category) || [])];
            const totalPcs = p.items?.reduce((s, i) => s + (i.qty_pcs_total || 0), 0) || 0;
            const totalNet = p.items?.reduce((s, i) => s + (i.qty_weight_net_kg || 0), 0) || 0;
            const primaryItem = p.items?.[0];

            return (
              <Card
                key={p.id}
                className="border-0 shadow-sm hover:shadow-md transition-all cursor-pointer overflow-hidden"
                style={{ borderRadius: '10px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}
                onClick={() => navigate(`/pallet/${p.pallet_ref}`)}
                data-testid={`pallet-card-${p.pallet_ref}`}
              >
                <CardContent className="p-3 flex gap-3 relative">
                  {/* 4px colored left border based on category */}
                  <div 
                    className="absolute left-0 top-0 bottom-0 w-1"
                    style={{
                      backgroundColor: categories.length === 1
                        ? CATEGORY_COLORS[categories[0]] || '#6b7280'
                        : '#1a2fce'
                    }}
                  />

                  <div className="flex-1 min-w-0 pl-2">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="font-mono font-bold text-sm text-gray-900">{p.pallet_ref}</span>
                      <Badge
                        className="text-[10px] px-1.5 py-0 font-medium text-white border-0"
                        style={{ backgroundColor: STATUS_COLORS[p.status] || '#6b7280' }}
                      >
                        {STATUS_LABELS[p.status] || p.status}
                      </Badge>
                      {p.status === 'qc_hold' && <Badge className="text-[10px] bg-red-600 text-white border-0">QC HOLD</Badge>}
                    </div>

                    <div className="text-xs text-gray-600 truncate mb-1">
                      {primaryItem?.description || 'No items'}
                      {p.items?.length > 1 && <span className="text-gray-400 ml-1">+{p.items.length - 1} more</span>}
                    </div>

                    <div className="flex items-center gap-3 text-xs text-gray-500">
                      {p.slot_address && (
                        <span className="flex items-center gap-1">
                          <MapPin size={11} /> {p.slot_address}
                        </span>
                      )}
                      <span>{totalPcs.toLocaleString()} pcs</span>
                      <span>{totalNet.toFixed(1)} kg</span>
                      {p.space_score > 0 && <span className="text-amber-600 font-medium">Score: {p.space_score}</span>}
                    </div>

                    {/* Category dots */}
                    <div className="flex items-center gap-1 mt-1.5">
                      {categories.slice(0, 4).map((cat) => (
                        <span key={cat} className="category-dot" style={{ backgroundColor: CATEGORY_COLORS[cat] || '#6b7280' }} title={CATEGORY_LABELS[cat]} />
                      ))}
                      {categories.length > 4 && (
                        <span className="text-[10px] text-gray-400 ml-0.5">+{categories.length - 4} more</span>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
